import { addDays, weekday } from "@talentkontor/domain";
import { ApiError, type Api } from "../../data/api";
import { currentLang, t, type Lang } from "../../lib/sprache";
import { monatTitel, tagKurz, tagLang, WOCHENTAGE, WOCHENTAGE_EN } from "../kommunikation/kalender/model";

/**
 * Team-Kalender der Betreiber-Konsole (Plan konsole-kalender.md, Migration 0530): reine Logik ohne React, mit Tests
 * (apps/web/test/konsole-kalender.test.ts). Alle Zeiten in deutscher Zeit (Europe/Berlin); der Feed liefert Tag und Uhrzeit
 * schon umgerechnet (starts_on, start_time …), deshalb rechnet hier nichts mit der Zeitzone des Browsers.
 * Bausteine des Agentur-Kalenders (Kommunikation › Kalender) werden übernommen: Monatsraster, Tagesnamen, Wochentage.
 */

export const ZONE = "Europe/Berlin";
export type Ansicht = "tag" | "woche" | "monat";
export type Sicht = "mein" | "team";
export type Art = "termin" | "call" | "abwesenheit" | "demo" | "demo_frei" | "aufgabe" | "naechster_schritt" | "testende" | "zahlung_offen" | "kuendigung" | "checkin";
export type Gruppe = "termine" | "abwesenheit" | "demo" | "vertrieb" | "fristen";
export type KonsolenRolleKurz = "developer" | "admin" | "sales";

/** Zeile aus public.staff_calendar_feed (0530) */
export interface KalEintrag {
  key: string;
  art: Art;
  gruppe: Gruppe;
  title: string | null;
  detail: string | null;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  starts_on: string;
  start_time: string | null;
  /** letzter Tag (einschließlich) bei ganztägigen Einträgen, sonst Tag des Endes */
  ends_on: string;
  end_time: string | null;
  user_id: string | null;
  private: boolean;
  belegt: boolean;
  editable: boolean;
  lead_id: string | null;
  org_id: string | null;
  demo_booking_id: string | null;
  demo_slot_id: string | null;
  location: string | null;
  meeting_url: string | null;
  note: string | null;
  absence_kind: "urlaub" | "krank" | "sonstiges" | null;
  source: string;
}
export interface Person { user_id: string; name: string; role: KonsolenRolleKurz; active: boolean; me: boolean; farbe: number }
export interface Feed {
  heute: string;
  zone: string;
  darf_fremde: boolean;
  darf_zeitfenster: boolean;
  personen: Person[];
  eintraege: KalEintrag[];
}

export const GRUPPEN: readonly { key: Gruppe; readonly label: string }[] = [
  { key: "termine", get label() { return t("Termine & Calls", "Appointments & calls"); } },
  { key: "abwesenheit", get label() { return t("Abwesenheit", "Away"); } },
  { key: "demo", get label() { return t("Demos", "Demos"); } },
  { key: "vertrieb", get label() { return t("Vertriebsaufgaben", "Sales tasks"); } },
  { key: "fristen", get label() { return t("Kundenfristen", "Customer deadlines"); } },
];
export const ALLE_GRUPPEN: readonly Gruppe[] = GRUPPEN.map((g) => g.key);

/** Farbe je Person (Punkt und Fläche des Termins, nie als Randstreifen): Tokens, die in hell und dunkel gelten */
export const PERSONEN_FARBEN: readonly string[] = ["var(--tk-s4)", "var(--tk-s2)", "var(--tk-s6)", "var(--tk-s5)", "var(--error)", "var(--info)", "var(--success)", "var(--warning)"];
export function personFarbe(personen: readonly Person[], userId: string | null): string {
  if (!userId) return "var(--control)";
  const p = personen.find((x) => x.user_id === userId);
  return p ? PERSONEN_FARBEN[((p.farbe % PERSONEN_FARBEN.length) + PERSONEN_FARBEN.length) % PERSONEN_FARBEN.length]! : "var(--control)";
}
export function personName(personen: readonly Person[], userId: string | null): string {
  if (!userId) return t("Team", "Team");
  const p = personen.find((x) => x.user_id === userId);
  return p ? `${p.name}${p.active ? "" : t(" (deaktiviert)", " (deactivated)")}` : t("unbekannt", "unknown");
}

export function artLabel(art: Art, lang: Lang = currentLang()): string {
  const en = lang === "en";
  switch (art) {
    case "termin": return en ? "Appointment" : "Termin";
    case "call": return "Call";
    case "abwesenheit": return en ? "Away" : "Abwesenheit";
    case "demo": return "Demo";
    case "demo_frei": return en ? "Open demo slot" : "Freies Demo-Zeitfenster";
    case "aufgabe": return en ? "Task" : "Aufgabe";
    case "naechster_schritt": return en ? "Next step" : "Nächster Schritt";
    case "testende": return en ? "Trial ends" : "Testende";
    case "zahlung_offen": return en ? "Payment overdue" : "Zahlung offen";
    case "kuendigung": return en ? "Cancellation effective" : "Kündigung wirksam";
    case "checkin": return "Check-in";
  }
}
const ABWESENHEIT: Record<string, [string, string]> = { urlaub: ["Urlaub", "Holiday"], krank: ["Krank", "Sick"], sonstiges: ["Abwesend", "Away"] };
export function abwesenheitLabel(k: string | null, lang: Lang = currentLang()): string {
  const x = ABWESENHEIT[k ?? "sonstiges"] ?? ABWESENHEIT.sonstiges!;
  return lang === "en" ? x[1] : x[0];
}

/** Titel im Kalender: private Einträge anderer ohne Inhalt, automatische Einträge mit ihrer Art vorne */
export function titel(e: Pick<KalEintrag, "art" | "title" | "detail" | "belegt">, lang: Lang = currentLang()): string {
  const en = lang === "en";
  if (e.belegt) return e.art === "abwesenheit" ? (en ? "Away" : "Abwesend") : (en ? "Busy" : "Belegt");
  const x = e.title?.trim() || "";
  switch (e.art) {
    case "termin": case "abwesenheit": return x;
    case "call": return `Call · ${x}`;
    case "demo_frei": return artLabel("demo_frei", lang);
    case "demo": return `Demo · ${[x, e.detail].filter(Boolean).join(", ")}`;
    case "aufgabe": case "naechster_schritt": return `${artLabel(e.art, lang)} · ${[x, e.detail].filter(Boolean).join(" · ")}`;
    default: return `${artLabel(e.art, lang)} · ${x}`;
  }
}

/** Wohin ein Klick führt: Lead, Konto oder Demo (Plan: „Klick auf einen Eintrag öffnet Lead, Konto oder Demo“) */
export function ziel(e: Pick<KalEintrag, "art" | "lead_id" | "org_id" | "demo_booking_id" | "demo_slot_id" | "belegt">, lang: Lang = currentLang()): { to: string; label: string } | null {
  const en = lang === "en";
  if (e.belegt) return null;
  if (e.art === "demo" || e.art === "demo_frei") return { to: "/intern/demo-termine", label: en ? "Open demo" : "Demo öffnen" };
  if (e.art === "testende" || e.art === "zahlung_offen" || e.art === "kuendigung" || e.art === "checkin") {
    if (e.org_id) return { to: `/intern/agenturen/${e.org_id}`, label: en ? "Open account" : "Konto öffnen" };
  }
  if (e.lead_id) return { to: `/intern/leads/${e.lead_id}`, label: en ? "Open lead" : "Lead öffnen" };
  if (e.org_id) return { to: `/intern/agenturen/${e.org_id}`, label: en ? "Open account" : "Konto öffnen" };
  if (e.demo_booking_id) return { to: "/intern/demo-termine", label: en ? "Open demo" : "Demo öffnen" };
  return null;
}
/** Eigene Einträge (Termin, Call, Abwesenheit) öffnen den Dialog; automatische führen direkt zu Lead, Konto oder Demo */
export const istEigener = (e: Pick<KalEintrag, "key">): boolean => e.key.startsWith("event:");
export const eventId = (e: Pick<KalEintrag, "key">): string => e.key.replace(/^event:/, "");

// ── Zeitraum je Ansicht ──────────────────────────────────────────────────────

export function wochenStart(tag: string): string {
  return addDays(tag, -((weekday(tag) + 6) % 7));
}
/** Tage der Ansicht (Woche Mo–So; Monat volle Wochen um den Monat) und Abfragebereich */
export function bereich(ansicht: Ansicht, datum: string): { von: string; bis: string; tage: string[] } {
  if (ansicht === "tag") return { von: datum, bis: datum, tage: [datum] };
  const start = ansicht === "woche" ? wochenStart(datum) : wochenStart(`${datum.slice(0, 7)}-01`);
  let ende = addDays(start, 6);
  if (ansicht === "monat") {
    const erster = `${datum.slice(0, 7)}-01`;
    const naechster = addDays(erster, 32).slice(0, 7);
    const letzter = addDays(`${naechster}-01`, -1);
    ende = addDays(wochenStart(letzter), 6);
  }
  const tage: string[] = [];
  for (let d = start; d <= ende; d = addDays(d, 1)) tage.push(d);
  return { von: start, bis: ende, tage };
}
/** vor/zurück je Ansicht */
export function blaettern(ansicht: Ansicht, datum: string, schritt: -1 | 1): string {
  if (ansicht === "tag") return addDays(datum, schritt);
  if (ansicht === "woche") return addDays(datum, 7 * schritt);
  const [y, m] = datum.slice(0, 7).split("-").map(Number) as [number, number];
  const z = new Date(Date.UTC(y, m - 1 + schritt, 1));
  return `${z.getUTCFullYear()}-${String(z.getUTCMonth() + 1).padStart(2, "0")}-01`;
}
export function kopfTitel(ansicht: Ansicht, datum: string, lang: Lang = currentLang()): string {
  if (ansicht === "tag") return tagLang(datum, lang);
  if (ansicht === "monat") return monatTitel(datum.slice(0, 7), lang);
  const { von, bis } = bereich("woche", datum);
  return `${lang === "en" ? "Week" : "KW"} ${kalenderwoche(von)} · ${tagKurz(von, lang)} – ${tagKurz(bis, lang)}${lang === "en" ? "/" : ""}${bis.slice(0, 4)}`;
}
/** Kalenderwoche nach ISO 8601 */
export function kalenderwoche(tag: string): number {
  const donnerstag = addDays(tag, 3 - ((weekday(tag) + 6) % 7));
  const jan4 = `${donnerstag.slice(0, 4)}-01-04`;
  const start = wochenStart(jan4);
  return 1 + Math.round((Date.parse(`${donnerstag}T12:00:00Z`) - Date.parse(`${start}T12:00:00Z`)) / (7 * 86_400_000));
}
export const wochentagKurz = (tag: string, lang: Lang = currentLang()): string => (lang === "en" ? WOCHENTAGE_EN : WOCHENTAGE)[(weekday(tag) + 6) % 7]!;

// ── Einträge je Tag und Lage im Zeitraster ─────────────────────────────────────

const minuten = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
/** Letzter Tag, an dem der Eintrag sichtbar ist (ein Termin bis 00:00 endet am Vortag) */
export function letzterTag(e: Pick<KalEintrag, "all_day" | "starts_on" | "ends_on" | "end_time">): string {
  if (e.all_day) return e.ends_on;
  if (e.end_time === "00:00" && e.ends_on > e.starts_on) return addDays(e.ends_on, -1);
  return e.ends_on;
}
export function amTag(list: readonly KalEintrag[], tag: string): KalEintrag[] {
  return list.filter((e) => e.starts_on <= tag && letzterTag(e) >= tag).sort(sortiere);
}
export function sortiere(a: KalEintrag, b: KalEintrag): number {
  if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
  return a.starts_at.localeCompare(b.starts_at) || a.key.localeCompare(b.key);
}
/** Ganztägig in dieser Ansicht: ganztägige Einträge und mehrtägige Termine an ihren mittleren Tagen */
export function alsGanztag(e: KalEintrag, tag: string): boolean {
  return e.all_day || (e.starts_on < tag && letzterTag(e) > tag);
}

/**
 * Lage eines Termins im Zeitraster. `bis` ist das echte Ende; `ende` das gezeichnete Ende: Kurze Termine bekommen mindestens
 * MIN_MINUTEN Höhe (Uhrzeit und Titel in zwei Zeilen statt gestaucht, Praxistest 04.11.2026), und die Spalten richten sich nach
 * dem gezeichneten Ende, damit sich Blöcke nie überdecken. `gruppe` zählt die Gruppen sich überschneidender Termine des Tages.
 */
export interface Lage { e: KalEintrag; von: number; bis: number; ende: number; spalte: number; spalten: number; gruppe: number }
export const MIN_MINUTEN = 45;
/** Termine mit Uhrzeit an einem Tag: Minuten seit 0 Uhr (an Folgetagen ab 0, am Vortag bis 24 Uhr) und Spalten bei Überschneidung */
export function lagen(list: readonly KalEintrag[], tag: string): Lage[] {
  const zeitlich = amTag(list, tag).filter((e) => !alsGanztag(e, tag));
  const roh = zeitlich.map((e) => {
    const von = e.starts_on < tag ? 0 : minuten(e.start_time ?? "00:00");
    const bis = Math.min(e.ends_on > tag ? 24 * 60 : Math.max(minuten(e.end_time ?? "00:00"), von + 15), 24 * 60);
    const ende = Math.min(Math.max(bis, von + MIN_MINUTEN), 24 * 60);
    return { e, von: Math.min(von, ende - 15), bis, ende, spalte: 0, spalten: 1, gruppe: 0 };
  }).sort((a, b) => a.von - b.von || b.ende - a.ende);
  // Gruppen sich überschneidender Termine nebeneinander
  let gruppe: Lage[] = [];
  let ende = -1;
  let nr = 0;
  const fertig = () => {
    const n = Math.max(1, ...gruppe.map((g) => g.spalte + 1));
    for (const g of gruppe) { g.spalten = n; g.gruppe = nr; }
    nr += 1;
    gruppe = [];
  };
  for (const l of roh) {
    if (l.von >= ende && gruppe.length) fertig();
    const belegt = new Set(gruppe.filter((g) => g.ende > l.von).map((g) => g.spalte));
    let s = 0;
    while (belegt.has(s)) s++;
    l.spalte = s;
    gruppe.push(l);
    ende = Math.max(ende, l.ende);
  }
  if (gruppe.length) fertig();
  return roh;
}

/** Mindestbreite eines Termins im Zeitraster (px; „● 10:00 Call ·“ passt in eine Zeile) und Breite der Spalte „+n“ */
export const MIN_BLOCK_PX = 90;
export const MEHR_PX = 28;
/** „+n“: Lage der Spalte (von, ende), Zeitraum der ganzen Gruppe (echte Zeiten) und alle Termine der Gruppe für die Liste */
export interface Mehr { key: string; von: number; ende: number; anzahl: number; zeitVon: number; zeitBis: number; eintraege: KalEintrag[] }
export interface Verteilung { sichtbar: (Lage & { mitMehr: boolean })[]; mehr: Mehr[] }
/**
 * Wie viele sich überschneidende Termine nebeneinander passen (Praxistest 04.11.2026): jeder mindestens MIN_BLOCK_PX breit, in der
 * Woche höchstens zwei. Passt eine Gruppe nicht, stehen die ersten Spalten da und rechts daneben „+n“; der Klick zeigt alle Termine
 * der Gruppe als Liste. `breite`: Breite einer Tagesspalte in px (null, solange unbekannt: dann gilt nur die Obergrenze).
 */
export function verteile(ls: readonly Lage[], breite: number | null, woche: boolean): Verteilung {
  const obergrenze = woche ? 2 : Number.POSITIVE_INFINITY;
  const gruppen = new Map<number, Lage[]>();
  for (const l of ls) gruppen.set(l.gruppe, [...(gruppen.get(l.gruppe) ?? []), l]);
  const sichtbar: Verteilung["sichtbar"] = [];
  const mehr: Mehr[] = [];
  for (const [nr, g] of gruppen) {
    const n = g[0]!.spalten;
    const passt = n <= obergrenze && (breite === null || n * MIN_BLOCK_PX <= breite);
    if (passt) {
      for (const l of g) sichtbar.push({ ...l, mitMehr: false });
      continue;
    }
    const k = Math.max(1, Math.min(obergrenze, breite === null ? obergrenze : Math.floor((breite - MEHR_PX) / MIN_BLOCK_PX), n - 1));
    for (const l of g) if (l.spalte < k) sichtbar.push({ ...l, spalten: k, mitMehr: true });
    const weg = g.filter((l) => l.spalte >= k);
    mehr.push({ key: `mehr-${nr}`, von: Math.min(...weg.map((l) => l.von)), ende: Math.max(...weg.map((l) => l.ende)), anzahl: weg.length,
      zeitVon: Math.min(...g.map((l) => l.von)), zeitBis: Math.max(...g.map((l) => l.bis)),
      eintraege: [...g].sort((a, b) => a.von - b.von || sortiere(a.e, b.e)).map((l) => l.e) });
  }
  return { sichtbar, mehr };
}
/** Zeilen für Uhrzeit und Titel eines Blocks (Höhe in px abzüglich Innenabstand und Rand 6 px, Zeilenhöhe 14 px, kalender.css) */
export const ZEILE_PX = 14;
export function titelZeilen(hoehePx: number): number {
  return Math.max(1, Math.floor((hoehePx - 6) / ZEILE_PX));
}
/** Sichtbare Stunden im Zeitraster: mindestens 7 bis 20 Uhr, erweitert um frühere oder spätere Termine */
export function stunden(lagenListe: readonly Lage[]): { von: number; bis: number } {
  let von = 7, bis = 20;
  for (const l of lagenListe) {
    von = Math.min(von, Math.floor(l.von / 60));
    bis = Math.max(bis, Math.ceil(l.ende / 60));
  }
  return { von: Math.max(0, von), bis: Math.min(24, bis) };
}

export function filtere(list: readonly KalEintrag[], gruppen: readonly Gruppe[], personen: readonly string[] | null): KalEintrag[] {
  return list.filter((e) => gruppen.includes(e.gruppe) && (!personen || !e.user_id || personen.includes(e.user_id)));
}

export function zeitText(e: Pick<KalEintrag, "all_day" | "starts_on" | "ends_on" | "start_time" | "end_time">, lang: Lang = currentLang()): string {
  const en = lang === "en";
  if (e.all_day) {
    return e.ends_on > e.starts_on ? `${tagKurz(e.starts_on, lang)} – ${tagKurz(e.ends_on, lang)}${en ? ", all day" : ", ganztägig"}` : (en ? "all day" : "ganztägig");
  }
  if (e.ends_on > e.starts_on) return `${tagKurz(e.starts_on, lang)} ${e.start_time} – ${tagKurz(e.ends_on, lang)} ${e.end_time}`;
  return `${e.start_time}–${e.end_time}${en ? "" : " Uhr"}`;
}

// ── Kachel „Heute“ auf der Startseite ──────────────────────────────────────────

/**
 * Nächste Calls und Demo-Calls (ab jetzt, bis 7 Tage), übrige Termine von heute (nur gezählt), fällige Aufgaben (bis heute, auch
 * überfällige), heute abwesend, Kundenfristen von heute bis in 3 Tagen. Praxistest 04.11.2026: normale Termine gehören nicht unter
 * „Calls“; sie stehen als eigene Zeile „Heute außerdem …“.
 */
export function heute(list: readonly KalEintrag[], heuteTag: string, jetzt: Date, max = 5): {
  calls: KalEintrag[]; termine: KalEintrag[]; aufgaben: KalEintrag[]; abwesend: KalEintrag[]; fristen: KalEintrag[];
} {
  const bisTag = addDays(heuteTag, 7);
  const kommt = (e: KalEintrag) => !e.all_day && !e.belegt && Date.parse(e.ends_at) > jetzt.getTime();
  const calls = list.filter((e) => (e.art === "call" || e.art === "demo") && kommt(e) && e.starts_on <= bisTag).sort(sortiere).slice(0, max);
  const termine = list.filter((e) => e.art === "termin" && kommt(e) && e.starts_on === heuteTag).sort(sortiere);
  const aufgaben = list.filter((e) => (e.art === "aufgabe" || e.art === "naechster_schritt") && e.starts_on <= heuteTag)
    .sort((a, b) => a.starts_on.localeCompare(b.starts_on) || a.key.localeCompare(b.key)).slice(0, max);
  const abwesend = list.filter((e) => e.art === "abwesenheit" && e.starts_on <= heuteTag && e.ends_on >= heuteTag);
  const fristen = kundenfristen(list, heuteTag, null, max);
  return { calls, termine, aufgaben, abwesend, fristen };
}
/**
 * Kundenfristen der Kachel aus derselben Quelle wie der Kalender (staff_calendar_feed, Team-Sicht): von heute bis in 3 Tagen.
 * `ich`: im Vertrieb nur eigene und solche ohne Zuständigen (die Sicht „mein“ ließe Fristen ohne Zuständigen weg, Praxistest
 * 04.11.2026); null: alle (Admin und Entwicklung).
 */
export function kundenfristen(list: readonly KalEintrag[], heuteTag: string, ich: string | null, max = 5): KalEintrag[] {
  return list.filter((e) => e.gruppe === "fristen" && e.starts_on >= heuteTag && e.starts_on <= addDays(heuteTag, 3) && (ich === null || !e.user_id || e.user_id === ich))
    .sort(sortiere).slice(0, max);
}

// ── Formular ─────────────────────────────────────────────────────────────────

export type FormArt = "termin" | "call" | "abwesenheit" | "zeitfenster";
export interface Formular {
  kind: FormArt;
  absence_kind: "urlaub" | "krank" | "sonstiges";
  title: string;
  all_day: boolean;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  /** Demo-Zeitfenster: Dauer in Minuten */
  minutes: string;
  private: boolean;
  user_id: string;
  lead_id: string;
  location: string;
  meeting_url: string;
  note: string;
}
const plusMinuten = (hhmm: string, n: number): string => {
  const m = Math.min(minuten(hhmm) + n, 23 * 60 + 59);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};
export function leeresFormular(datum: string, zeit: string | null, ich: string, kind: FormArt = "termin"): Formular {
  const start = zeit ?? "09:00";
  return { kind, absence_kind: "urlaub", title: "", all_day: kind === "abwesenheit", start_date: datum, start_time: start, end_date: datum,
    end_time: plusMinuten(start, kind === "call" || kind === "zeitfenster" ? 30 : 60), minutes: "30", private: false, user_id: ich, lead_id: "",
    location: "", meeting_url: "", note: "" };
}
/** Eigener Eintrag → Formular; die Datenbank liefert ganztägig den letzten Tag einschließlich */
export function formularAus(e: KalEintrag): Formular {
  return { kind: e.art === "call" ? "call" : e.art === "abwesenheit" ? "abwesenheit" : "termin", absence_kind: e.absence_kind ?? "urlaub",
    title: e.title ?? "", all_day: e.all_day, start_date: e.starts_on, start_time: e.start_time ?? "09:00", end_date: e.ends_on,
    end_time: e.end_time ?? "10:00", minutes: "30", private: e.private, user_id: e.user_id ?? "", lead_id: e.lead_id ?? "",
    location: e.location ?? "", meeting_url: e.meeting_url ?? "", note: e.note ?? "" };
}
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const HM = /^\d{2}:\d{2}$/;
/** Prüfung vor dem Speichern (die Datenbank prüft dieselben Regeln noch einmal, auch die Zeitumstellung) */
export function pruefe(f: Formular): { fehler: Record<string, string>; daten: Record<string, unknown> | null } {
  const fehler: Record<string, string> = {};
  const ganz = f.kind === "abwesenheit" || (f.kind !== "zeitfenster" && f.all_day);
  if (!ISO.test(f.start_date)) fehler.start_date = t("Bitte ein Datum wählen.", "Please choose a date.");
  if (f.kind === "zeitfenster") {
    if (!HM.test(f.start_time)) fehler.start_time = t("Bitte eine Uhrzeit wählen.", "Please choose a time.");
    const n = Number(f.minutes);
    if (!Number.isInteger(n) || n < 15 || n > 180) fehler.minutes = t("Die Dauer muss zwischen 15 und 180 Minuten liegen.", "Duration must be between 15 and 180 minutes.");
    if (f.meeting_url.trim() && !/^https:\/\/\S+$/.test(f.meeting_url.trim())) fehler.meeting_url = t("Der Link muss mit https:// beginnen.", "The link must start with https://.");
    if (Object.keys(fehler).length) return { fehler, daten: null };
    return { fehler, daten: { day: f.start_date, time: f.start_time, minutes: n, ...(f.meeting_url.trim() ? { meeting_url: f.meeting_url.trim() } : {}) } };
  }
  const ende = f.end_date || f.start_date;
  if (!ISO.test(ende)) fehler.end_date = t("Bitte ein Datum wählen.", "Please choose a date.");
  else if (ende < f.start_date) fehler.end_date = t("Das Ende liegt vor dem Beginn.", "The end is before the start.");
  if (!ganz) {
    if (!HM.test(f.start_time)) fehler.start_time = t("Bitte eine Uhrzeit wählen.", "Please choose a time.");
    if (!HM.test(f.end_time)) fehler.end_time = t("Bitte eine Uhrzeit wählen.", "Please choose a time.");
    else if (ende === f.start_date && HM.test(f.start_time) && f.end_time <= f.start_time) fehler.end_time = t("Das Ende muss nach dem Beginn liegen.", "The end must be after the start.");
  }
  if (f.kind !== "abwesenheit" && !f.title.trim()) fehler.title = t("Bitte einen Titel angeben.", "Please enter a title.");
  if (f.title.trim().length > 200) fehler.title = t("Höchstens 200 Zeichen.", "At most 200 characters.");
  if (f.meeting_url.trim() && !/^https:\/\/\S+$/.test(f.meeting_url.trim())) fehler.meeting_url = t("Der Link muss mit https:// beginnen.", "The link must start with https://.");
  if (Object.keys(fehler).length) return { fehler, daten: null };
  return {
    fehler,
    daten: {
      kind: f.kind, title: f.title.trim(), all_day: ganz, start_date: f.start_date, end_date: ende,
      ...(ganz ? {} : { start_time: f.start_time, end_time: f.end_time }),
      ...(f.kind === "abwesenheit" ? { absence_kind: f.absence_kind } : {}),
      private: f.private, user_id: f.user_id || null, lead_id: f.kind === "abwesenheit" ? null : f.lead_id || null,
      location: f.location.trim() || null, meeting_url: f.meeting_url.trim() || null, note: f.note.trim() || null,
    },
  };
}

// ── Daten ───────────────────────────────────────────────────────────────────

type Reader = Pick<Api, "read">;
type Writer = Pick<Api, "rpc">;
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
const kennung = (r: unknown): string => String(r).replace(/^"|"$/g, "");

export const FEED_KEY = "staff_calendar_feed";
export async function ladeFeed(api: Reader, von: string, bis: string, sicht: Sicht): Promise<Feed> {
  return parse<Feed>(await api.read("staff_calendar_feed", { p_from: von, p_to: bis, p_scope: sicht }));
}
export async function speichereEintrag(api: Writer, id: string | null, daten: Record<string, unknown>): Promise<string> {
  return kennung(await api.rpc("staff_event_save", { p_id: id, p: daten }));
}
export async function loescheEintrag(api: Writer, id: string): Promise<void> {
  await api.rpc("staff_event_delete", { p_id: id });
}
export async function legeZeitfensterAn(api: Writer, daten: Record<string, unknown>): Promise<void> {
  await api.rpc("platform_demo_slots_create", { p_slots: [daten] });
}
export async function aboStand(api: Reader): Promise<{ created_at: string; last_used_at: string | null } | null> {
  return parse<{ created_at: string; last_used_at: string | null } | null>(await api.read("staff_calendar_link_status", {})) ?? null;
}
export async function aboErzeugen(api: Writer): Promise<string> {
  return kennung(await api.rpc("staff_calendar_link_create", {}));
}
export async function aboBeenden(api: Writer): Promise<void> {
  await api.rpc("staff_calendar_link_revoke", {});
}
/** Adressen des Abos (Serverfunktion calendar-ics); in der Demo gibt es keinen Server dafür */
export function aboAdressen(token: string, base: string | undefined): { mein: string; team: string } | null {
  if (!base) return null;
  const u = `${base.replace(/\/+$/, "")}/functions/v1/calendar-ics?s=${token}`;
  return { mein: u, team: `${u}&team=1` };
}
export const istVerboten = (e: unknown): boolean => e instanceof ApiError && e.forbidden;
